import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery, useLazyQuery, useApolloClient } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Navigation } from '../components/Navigation';
import { Breadcrumb } from '../components/Breadcrumb';
import { BulkActions } from '../components/admin/AdminActions';
import { DocumentManagementActions } from '../components/admin/DocumentManagementActions';
import { DocumentThumbnail } from '../components/DocumentThumbnail';
import { FileViewerModal, FileItem } from '../components/FileViewerModal';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { format } from 'date-fns';

interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface Case {
  id: string;
  caseNumber: string;
  clientId: string;
  client?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  assignedAttorneyId?: string;
  assignedCaseManagerId?: string;
  status: string;
  createdAt: string;
}

interface ListCasesData {
  listCases: {
    items: Case[];
  };
}

interface Document {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  description?: string;
  createdAt: string;
  uploadedBy?: string;
  uploader?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

interface ListDocumentsByCaseData {
  listDocumentsByCase: Document[];
}

interface GetDocumentDownloadUrlData {
  getDocumentDownloadUrl: string;
}

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const LIST_CASES = gql`
  query ListCases {
    listCases {
      items {
        id
        caseNumber
        clientId
        client {
          id
          firstName
          lastName
        }
        assignedAttorneyId
        assignedCaseManagerId
        status
        createdAt
      }
    }
  }
`;

const LIST_DOCUMENTS_BY_CASE = gql`
  query ListDocumentsByCase($caseId: ID!, $includeDeleted: Boolean) {
    listDocumentsByCase(caseId: $caseId, includeDeleted: $includeDeleted) {
      id
      fileName
      fileType
      fileSize
      description
      createdAt
      uploadedBy
      uploader {
        id
        firstName
        lastName
      }
      isDeleted
      deletedAt
      deletedBy
    }
  }
`;

const GET_DOCUMENT_DOWNLOAD_URL = gql`
  query GetDocumentDownloadUrl($id: ID!) {
    getDocumentDownloadUrl(id: $id)
  }
`;

type SortField = 'fileName' | 'fileType' | 'fileSize' | 'caseNumber' | 'createdAt';
type SortDirection = 'asc' | 'desc';
type ViewDensity = 'compact' | 'comfortable' | 'spacious';

export function DocumentsList() {
  // Data
  const [allDocuments, setAllDocuments] = useState<any[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  
  // File viewer state
  const [fileViewerVisible, setFileViewerVisible] = useState(false);
  const [fileViewerFiles, setFileViewerFiles] = useState<FileItem[]>([]);
  const [fileViewerIndex, setFileViewerIndex] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [caseFilter, setCaseFilter] = useState<string>('');
  const [fileTypeFilter, setFileTypeFilter] = useState<string>('all');
  const [showOnlyAssigned, setShowOnlyAssigned] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  
  // Table controls
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [viewDensity, setViewDensity] = useState<ViewDensity>('comfortable');
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const handleBulkExport = () => {
    // Get all filtered documents (not just current page)
    let filteredDocs = allDocuments.filter((doc: any) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          doc.fileName.toLowerCase().includes(query) ||
          doc.caseNumber.toLowerCase().includes(query) ||
          doc.clientName.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      if (caseFilter && doc.caseId !== caseFilter) return false;
      
      if (fileTypeFilter !== 'all') {
        const docType = getFileType(doc.fileName);
        if (docType !== fileTypeFilter) return false;
      }
      
      if (showOnlyAssigned && user) {
        const isAssigned = 
          doc.assignedAttorneyId === user.id ||
          doc.assignedCaseManagerId === user.id ||
          user.role === 'ADMIN';
        if (!isAssigned) return false;
      }
      
      return true;
    });

    const selectedData = filteredDocs.filter(d => selectedDocuments.has(d.id));
    const csvContent = [
      ['File Name', 'Case Number', 'Client', 'File Type', 'File Size', 'Created Date'].join(','),
      ...selectedData.map(d => [
        `"${d.fileName}"`,
        d.caseNumber,
        `"${d.clientName}"`,
        getFileType(d.fileName),
        formatFileSize(d.fileSize),
        format(new Date(d.createdAt), 'yyyy-MM-dd')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `selected-documents-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDocuments(new Set(paginatedDocuments.map((d: any) => d.id)));
    } else {
      setSelectedDocuments(new Set());
    }
  };

  const handleSelectDocument = (documentId: string, checked: boolean) => {
    const newSelected = new Set(selectedDocuments);
    if (checked) {
      newSelected.add(documentId);
    } else {
      newSelected.delete(documentId);
    }
    setSelectedDocuments(newSelected);
  };

  const { data: userData, loading: userLoading } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const { data: casesData, loading: casesLoading } = useQuery<ListCasesData>(LIST_CASES);
  const apolloClient = useApolloClient();
  const [getDownloadUrl] = useLazyQuery<GetDocumentDownloadUrlData>(GET_DOCUMENT_DOWNLOAD_URL);
  const fetchingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const user = userData?.getCurrentUser;
  const cases = casesData?.listCases?.items || [];
  const isAdmin = user?.role === 'ADMIN';

  // Fetch documents for all cases with proper abort handling
  const fetchAllDocuments = useCallback(async () => {
    if (cases.length === 0 || fetchingRef.current) return;
    
    // Cancel any previous fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    fetchingRef.current = true;
    setLoadingDocs(true);
    
    const allDocs: any[] = [];
    const batchSize = 5; // Fetch 5 cases at a time to balance speed and stability
    
    try {
      for (let i = 0; i < cases.length; i += batchSize) {
        // Check if aborted
        if (abortControllerRef.current?.signal.aborted) {
          break;
        }
        
        const batch = cases.slice(i, i + batchSize);
        const batchPromises = batch.map(async (caseItem: any) => {
          try {
            const { data } = await apolloClient.query<ListDocumentsByCaseData>({
              query: LIST_DOCUMENTS_BY_CASE,
              variables: { 
                caseId: caseItem.id,
                includeDeleted: isAdmin
              },
              fetchPolicy: 'network-only',
            });
            
            const docs = data?.listDocumentsByCase || [];
            
            return docs.map((doc: any) => ({
              ...doc,
              caseId: caseItem.id,
              caseNumber: caseItem.caseNumber,
              clientName: `${caseItem.client?.firstName || ''} ${caseItem.client?.lastName || ''}`.trim(),
              assignedAttorneyId: caseItem.assignedAttorneyId,
              assignedCaseManagerId: caseItem.assignedCaseManagerId,
            }));
          } catch (error: any) {
            // Silently ignore abort errors
            if (error?.name !== 'AbortError' && !error?.message?.includes('aborted')) {
              console.error(`Error fetching documents for case ${caseItem.id}:`, error);
            }
            return [];
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        allDocs.push(...batchResults.flat());
      }
      
      // Only update state if not aborted
      if (!abortControllerRef.current?.signal.aborted) {
        setAllDocuments(allDocs);
      }
    } finally {
      fetchingRef.current = false;
      setLoadingDocs(false);
    }
  }, [cases, isAdmin, apolloClient]);

  useEffect(() => {
    // Only fetch when we have cases and user data
    if (cases.length > 0 && user) {
      fetchAllDocuments();
    }
    
    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [cases.length, isAdmin, user, fetchAllDocuments]);

  // Get unique file types for filter
  const fileTypes = useMemo(() => {
    const types = new Set<string>();
    allDocuments.forEach((doc: any) => {
      if (doc.fileType.includes('pdf')) types.add('PDF');
      else if (doc.fileType.includes('image')) types.add('Image');
      else if (doc.fileType.includes('word') || doc.fileType.includes('doc')) types.add('Word');
      else if (doc.fileType.includes('excel') || doc.fileType.includes('sheet')) types.add('Excel');
      else types.add('Other');
    });
    return Array.from(types).sort();
  }, [allDocuments]);

  // Filtering, sorting, and pagination
  const { paginatedDocuments, totalPages, totalFiltered, stats } = useMemo(() => {
    let filtered = allDocuments.filter((doc: any) => {
      // Filter deleted documents (unless admin has enabled showDeleted)
      if (!showDeleted && doc.isDeleted) return false;
      
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          doc.fileName.toLowerCase().includes(query) ||
          doc.caseNumber.toLowerCase().includes(query) ||
          doc.clientName.toLowerCase().includes(query) ||
          doc.description?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Case filter
      if (caseFilter && doc.caseId !== caseFilter) return false;
      
      // File type filter
      if (fileTypeFilter !== 'all') {
        if (fileTypeFilter === 'PDF' && !doc.fileType.includes('pdf')) return false;
        if (fileTypeFilter === 'Image' && !doc.fileType.includes('image')) return false;
        if (fileTypeFilter === 'Word' && !doc.fileType.includes('word') && !doc.fileType.includes('doc')) return false;
        if (fileTypeFilter === 'Excel' && !doc.fileType.includes('excel') && !doc.fileType.includes('sheet')) return false;
        if (fileTypeFilter === 'Other') {
          if (doc.fileType.includes('pdf') || doc.fileType.includes('image') || 
              doc.fileType.includes('word') || doc.fileType.includes('doc') ||
              doc.fileType.includes('excel') || doc.fileType.includes('sheet')) {
            return false;
          }
        }
      }
      
      // Assignment filter
      if (showOnlyAssigned && user) {
        const isAssigned = 
          doc.assignedAttorneyId === user.id ||
          doc.assignedCaseManagerId === user.id ||
          user.role === 'ADMIN';
        if (!isAssigned) return false;
      }
      
      return true;
    });

    // Sort
    filtered.sort((a: any, b: any) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'fileName':
          aVal = a.fileName.toLowerCase();
          bVal = b.fileName.toLowerCase();
          break;
        case 'fileType':
          aVal = a.fileType.toLowerCase();
          bVal = b.fileType.toLowerCase();
          break;
        case 'fileSize':
          aVal = a.fileSize;
          bVal = b.fileSize;
          break;
        case 'caseNumber':
          aVal = a.caseNumber.toLowerCase();
          bVal = b.caseNumber.toLowerCase();
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Calculate stats
    const totalSize = filtered.reduce((sum, doc) => sum + doc.fileSize, 0);
    const pdfCount = filtered.filter(doc => doc.fileType.includes('pdf')).length;
    const imageCount = filtered.filter(doc => doc.fileType.includes('image')).length;
    const uniqueCases = new Set(filtered.map(doc => doc.caseId)).size;

    // Pagination
    const totalFiltered = filtered.length;
    const totalPages = Math.ceil(totalFiltered / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedDocuments = filtered.slice(startIndex, endIndex);

    return { 
      paginatedDocuments, 
      totalPages, 
      totalFiltered,
      stats: {
        total: allDocuments.length,
        active: allDocuments.filter(d => !d.isDeleted).length,
        deleted: allDocuments.filter(d => d.isDeleted).length,
        totalSize,
        pdfCount,
        imageCount,
        uniqueCases,
      }
    };
  }, [allDocuments, searchQuery, caseFilter, fileTypeFilter, showOnlyAssigned, showDeleted, user, sortField, sortDirection, currentPage, pageSize]);

  // Reset to page 1 when filters change
  const handleFilterChange = (setter: any) => (value: any) => {
    setter(value);
    setCurrentPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setCaseFilter('');
    setFileTypeFilter('all');
    setShowOnlyAssigned(false);
    setShowDeleted(false);
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handleDownload = async (documentId: string) => {
    try {
      const { data } = await getDownloadUrl({
        variables: { id: documentId },
      });

      if (data?.getDocumentDownloadUrl) {
        window.open(data.getDocumentDownloadUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Error downloading document:', error);
      alert('Error downloading document: ' + error.message);
    }
  };

  const handlePreviewDocument = async (doc: any, index: number) => {
    try {
      // Get download URLs for all visible documents to enable navigation
      const filesPromises = paginatedDocuments.map(async (d: any) => {
        const { data } = await getDownloadUrl({ variables: { id: d.id } });
        return {
          url: data?.getDocumentDownloadUrl || '',
          fileName: d.fileName,
          fileType: d.fileType,
        };
      });
      
      const files = await Promise.all(filesPromises);
      setFileViewerFiles(files.filter(f => f.url));
      setFileViewerIndex(index);
      setFileViewerVisible(true);
    } catch (error: any) {
      console.error('Error loading document preview:', error);
      // Fallback to download
      handleDownload(doc.id);
    }
  };

  const handleExport = () => {
    // Export ALL filtered documents, not just current page
    const allFilteredDocs = allDocuments.filter((doc: any) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          doc.fileName.toLowerCase().includes(query) ||
          doc.caseNumber.toLowerCase().includes(query) ||
          doc.clientName.toLowerCase().includes(query) ||
          doc.description?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      if (caseFilter && doc.caseId !== caseFilter) return false;
      if (fileTypeFilter !== 'all') {
        if (fileTypeFilter === 'PDF' && !doc.fileType.includes('pdf')) return false;
        if (fileTypeFilter === 'Image' && !doc.fileType.includes('image')) return false;
        if (fileTypeFilter === 'Word' && !doc.fileType.includes('word') && !doc.fileType.includes('doc')) return false;
        if (fileTypeFilter === 'Excel' && !doc.fileType.includes('excel') && !doc.fileType.includes('sheet')) return false;
        if (fileTypeFilter === 'Other') {
          if (doc.fileType.includes('pdf') || doc.fileType.includes('image') || 
              doc.fileType.includes('word') || doc.fileType.includes('doc') ||
              doc.fileType.includes('excel') || doc.fileType.includes('sheet')) {
            return false;
          }
        }
      }
      if (showOnlyAssigned && user) {
        const isAssigned = 
          doc.assignedAttorneyId === user.id ||
          doc.assignedCaseManagerId === user.id ||
          user.role === 'ADMIN';
        if (!isAssigned) return false;
      }
      return true;
    });

    const csvContent = [
      ['File Name', 'Case Number', 'Client', 'File Type', 'File Size', 'Uploaded By', 'Created'].join(','),
      ...allFilteredDocs.map((doc: any) => [
        `"${doc.fileName}"`,
        doc.caseNumber,
        `"${doc.clientName}"`,
        doc.fileType,
        formatFileSize(doc.fileSize),
        doc.uploadedBy || '',
        format(new Date(doc.createdAt), 'yyyy-MM-dd'),
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `documents-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const getFileType = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    if (!extension) return 'Other';
    
    if (extension === 'pdf') return 'PDF';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) return 'Image';
    if (['doc', 'docx'].includes(extension)) return 'Word';
    if (['xls', 'xlsx'].includes(extension)) return 'Excel';
    return 'Other';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatTotalSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const densityClasses = {
    compact: 'py-2',
    comfortable: 'py-3',
    spacious: 'py-4',
  };

  if (userLoading || casesLoading || loadingDocs) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
      </div>
    );
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb />

        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-16 z-40">
          <div className="max-w-full px-6">
            <div className="flex items-center justify-between h-14">
              <div className="flex items-center gap-4">
                <h1 className="text-lg font-semibold text-aalaw-charcoal">Documents</h1>
                <Badge variant="secondary" className="text-xs">
                  {totalFiltered.toLocaleString()} {totalFiltered === 1 ? 'document' : 'documents'}
                  {totalFiltered !== allDocuments.length && ` of ${allDocuments.length.toLocaleString()} total`}
                </Badge>
                {totalFiltered > 0 && (
                  <span className="text-xs text-gray-500">
                    Page {currentPage} of {totalPages}
                  </span>
                )}
              </div>
            <div className="flex items-center gap-2">
              {/* View Density */}
              <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                {(['compact', 'comfortable', 'spacious'] as ViewDensity[]).map((density) => (
                  <button
                    key={density}
                    onClick={() => setViewDensity(density)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      viewDensity === density
                        ? 'bg-gray-200 text-gray-900'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    title={density.charAt(0).toUpperCase() + density.slice(1)}
                  >
                    {density === 'compact' && '━'}
                    {density === 'comfortable' && '≡'}
                    {density === 'spacious' && '☰'}
                  </button>
                ))}
              </div>
              
              {/* Page Size Selector */}
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="h-8 px-2 text-xs border border-gray-300 rounded-lg"
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={200}>200 per page</option>
              </select>
              
              <Button variant="outline" size="sm" onClick={handleExport}>
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-full px-6 py-6">
        {/* Stats Bar */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.active.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Active Documents</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-purple-600">{formatTotalSize(stats.totalSize)}</p>
                  <p className="text-sm text-gray-600">Total Size</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.pdfCount.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">PDF Files</p>
                </div>
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.uniqueCases.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Cases</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Admin: Show Deleted Toggle */}
        {isAdmin && stats.deleted > 0 && (
          <Card className="bg-amber-50 border-amber-200 mb-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.732 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-amber-900">
                      {stats.deleted} deleted {stats.deleted === 1 ? 'document' : 'documents'}
                    </p>
                    <p className="text-xs text-amber-700">
                      Soft-deleted documents are hidden by default
                    </p>
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-sm font-medium text-amber-900">Show Deleted</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={showDeleted}
                      onChange={(e) => setShowDeleted(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
                  </div>
                </label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters Bar */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-12 gap-3">
              {/* Search */}
              <div className="col-span-3">
                <Input
                  type="text"
                  placeholder="Search files, cases, clients..."
                  value={searchQuery}
                  onChange={(e) => handleFilterChange(setSearchQuery)(e.target.value)}
                  className="w-full h-9 text-sm"
                />
              </div>

              {/* Case Filter */}
              <div className="col-span-3">
                <select
                  value={caseFilter}
                  onChange={(e) => handleFilterChange(setCaseFilter)(e.target.value)}
                  className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="">All Cases</option>
                  {cases.map((caseItem: any) => (
                    <option key={caseItem.id} value={caseItem.id}>
                      {caseItem.caseNumber} - {caseItem.client?.firstName} {caseItem.client?.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* File Type */}
              <div className="col-span-2">
                <select
                  value={fileTypeFilter}
                  onChange={(e) => handleFilterChange(setFileTypeFilter)(e.target.value)}
                  className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="all">All File Types</option>
                  {fileTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* My Cases Toggle */}
              <div className="col-span-3 flex items-center">
                <label className="flex items-center cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={showOnlyAssigned}
                    onChange={(e) => handleFilterChange(setShowOnlyAssigned)(e.target.checked)}
                    className="w-4 h-4 text-aalaw-red border-gray-300 rounded focus:ring-aalaw-red mr-2"
                  />
                  My Assigned Cases Only
                </label>
              </div>

              {/* Clear Filters */}
              <div className="col-span-1 flex items-center justify-end">
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 hover:text-aalaw-red"
                  title="Clear all filters"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        <BulkActions
          userRole={user?.role}
          resource="documents"
          selectedCount={selectedDocuments.size}
          onBulkEdit={() => console.log('Bulk edit documents')}
          onBulkExport={handleBulkExport}
          className="mb-4"
        />

        {/* Documents Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedDocuments.size === paginatedDocuments.length && paginatedDocuments.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 text-aalaw-red border-gray-300 rounded focus:ring-aalaw-red"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                    Type
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('fileName')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      File Name
                      <SortIcon field="fileName" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('caseNumber')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Case
                      <SortIcon field="caseNumber" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                    Client
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('fileSize')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Size
                      <SortIcon field="fileSize" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                    Uploaded By
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('createdAt')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Created
                      <SortIcon field="createdAt" />
                    </button>
                  </th>
                  <th className="w-16 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedDocuments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12">
                      <p className="text-gray-500">No documents found</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {searchQuery || caseFilter || fileTypeFilter !== 'all' || showOnlyAssigned
                          ? 'Try adjusting your filters'
                          : 'Documents will appear here once uploaded'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  paginatedDocuments.map((doc: any, index: number) => (
                    <tr
                      key={doc.id}
                      className={`hover:bg-gray-50 transition-colors group ${doc.isDeleted ? 'bg-red-50' : ''}`}
                    >
                      <td className={`px-4 ${densityClasses[viewDensity]}`} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedDocuments.has(doc.id)}
                          onChange={(e) => handleSelectDocument(doc.id, e.target.checked)}
                          className="w-4 h-4 text-aalaw-red border-gray-300 rounded focus:ring-aalaw-red"
                        />
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <DocumentThumbnail
                          document={doc}
                          getDownloadUrl={getDownloadUrl}
                          size={40}
                          onClick={() => handlePreviewDocument(doc, index)}
                        />
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <div className="text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handlePreviewDocument(doc, index)}
                              className={`font-medium truncate max-w-xs hover:text-aalaw-red transition-colors text-left ${doc.isDeleted ? 'text-gray-500 line-through' : 'text-gray-900'}`}
                            >
                              {doc.fileName}
                            </button>
                            {doc.isDeleted && (
                              <Badge variant="destructive" className="text-xs">Deleted</Badge>
                            )}
                          </div>
                          {doc.description && (
                            <div className="text-xs text-gray-500 truncate max-w-xs">
                              {doc.description}
                            </div>
                          )}
                          {doc.isDeleted && doc.deletedAt && (
                            <div className="text-xs text-red-600 mt-1">
                              Deleted {format(new Date(doc.deletedAt), 'MMM d, yyyy')}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <span className="text-sm font-mono text-aalaw-red">
                          {doc.caseNumber}
                        </span>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <span className="text-sm text-gray-700">
                          {doc.clientName || '—'}
                        </span>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <span className="text-sm text-gray-700">
                          {formatFileSize(doc.fileSize)}
                        </span>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <span className="text-sm text-gray-700">
                          {doc.uploader ? `${doc.uploader.firstName} ${doc.uploader.lastName}` : '—'}
                        </span>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <div className="text-sm text-gray-700">
                          {format(new Date(doc.createdAt), 'MMM d, yyyy')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {format(new Date(doc.createdAt), 'h:mm a')}
                        </div>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <DocumentManagementActions
                          documentId={doc.id}
                          documentName={doc.fileName}
                          userRole={user?.role}
                          isDeleted={doc.isDeleted}
                          onDownload={() => handleDownload(doc.id)}
                          onRefresh={fetchAllDocuments}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalFiltered)} of {totalFiltered.toLocaleString()} documents
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {getPageNumbers().map((page, index) => (
                      typeof page === 'string' ? (
                        <span key={`ellipsis-${index}`} className="px-2 text-gray-500">{page}</span>
                      ) : (
                        <button
                          key={`page-${page}`}
                          onClick={() => goToPage(page)}
                          className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                            currentPage === page
                              ? 'bg-aalaw-red text-white font-semibold'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ))}
                  </div>
                  
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </main>
      </div>

      {/* File Viewer Modal */}
      <FileViewerModal
        visible={fileViewerVisible}
        files={fileViewerFiles}
        initialIndex={fileViewerIndex}
        onClose={() => setFileViewerVisible(false)}
      />
    </div>
  );
}
